import { clsx } from 'clsx';
import { useCallback } from 'react';

import { Link } from 'Services/router';
import { COMMENTS_HASH_ID } from 'Shared/Comments/constants';
import { smoothScrollToElementBySelector } from 'Utils/dom/smoothScrollTo/smoothScrollToElementBySelector';
import { trackCommentsClick } from 'Shared/PostBody/biEvents';
import CommentSvg from 'Shared/svg/comment.svg';
import { SCROLL_TO_COMMENTS_HASH } from 'Shared/constants/scrollHashes';

import type { ClassNameT } from 'UI/types';
import type { BISourceT } from 'Shared/PostBody/types';

type PropsT = {
  articleId: string;
  biSource?: BISourceT;
  commentsCount: number;
  dataTestId?: string;
  hideLabel?: boolean;
  isAlwaysDark?: boolean;
  rootClassName?: ClassNameT;
};

export const CommentsButton: React.FunctionComponent<PropsT> = ({
  articleId,
  biSource,
  commentsCount,
  dataTestId,
  hideLabel,
  isAlwaysDark,
  rootClassName,
}) => {
  const handleClick = useCallback(() => {
    smoothScrollToElementBySelector({
      querySelector: `#${COMMENTS_HASH_ID}`,
    });
    if (biSource) {
      trackCommentsClick(articleId, biSource);
    }
  }, [articleId, biSource]);

  return (
    <Link
      className={clsx('flex items-center hover:no-underline', rootClassName)}
      data-test-id={dataTestId}
      to={SCROLL_TO_COMMENTS_HASH}
      onClick={handleClick}
    >
      <CommentSvg
        className={clsx(
          'h-18',
          isAlwaysDark ? 'stroke-blue-70' : 'stroke-share-link',
        )}
      />

      <div
        className={clsx(
          'ml-12 text-medium-b',
          hideLabel && 'sr-only',
          isAlwaysDark ? 'text-blue-70' : 'text-share-link',
        )}
      >
        {commentsCount === 1 ? 'Comment' : 'Comments'}
      </div>

      {commentsCount ? (
        <div
          className={clsx(
            'text-medium-r',
            isAlwaysDark ? 'text-black-30' : 'text-share-text-2',
            hideLabel ? 'ml-8' : 'ml-4',
          )}
        >
          {`(${commentsCount})`}
        </div>
      ) : null}
    </Link>
  );
};
